import { createElement, Fragment } from "react"
import Box from "@mui/material/Box"
import Checkbox from "@mui/material/Checkbox"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormLabel from "@mui/material/FormLabel"
import Typography from "@mui/material/Typography"
import Input from "../Input"
import SelectInput from "../SelectInput"
import SegmentedInput from "../SegmentedInput"
import { MuiOtpInput } from "mui-one-time-password-input"
import UploadImage from "../UploadImage"
import UploadImageV2 from "../UploadImageV2"
import MultiInputUploadImage from "../UploadImage/MultiInputUploadImage"

type FormProps = {
    data: any
    formik: any
}

const Form = (props: FormProps) => {

    const { data, formik } = props

    const renderInput = (field: any) => {
        switch (field.type) {
            case "row":
                return <Form {...{ data: field, formik }} />

            // An advisory banner, not an input. Pair with `visibleIf` to surface
            // guidance only when the answers warrant it.
            case "note":
                return (
                    <Box
                        sx={{
                            borderRadius: "8px",
                            padding: "14px 16px",
                            fontSize: "13px",
                            lineHeight: 1.55,
                            border: "1px solid",
                            ...(field?.variant === "danger"
                                ? { borderColor: "#fca5a5", background: "#fef2f2", color: "#7f1d1d" }
                                : { borderColor: "#fcd34d", background: "#fffbeb", color: "#78350f" }),
                        }}
                    >
                        {field?.text}
                    </Box>
                )

            case "checkbox":
                return (
                    <FormControlLabel
                        sx={{ alignItems: "flex-start", margin: 0, gap: "8px" }}
                        control={
                            <Checkbox
                                id={field.name}
                                name={field.name}
                                checked={!!formik.values[field.name]}
                                onChange={formik.handleChange}
                                disabled={field?.disabled}
                                sx={{ padding: 0, marginTop: "2px" }}
                            />
                        }
                        label={
                            <Box>
                                <Typography sx={{ fontSize: "14px", lineHeight: 1.5 }}>
                                    {field.label}
                                </Typography>
                                {formik.touched[field.name] && props.formik.errors[field.name] && (
                                    <Typography className="error_message" sx={{ marginTop: "4px" }}>
                                        {props.formik.errors[field.name]}
                                    </Typography>
                                )}
                            </Box>
                        }
                    />
                )

            // A fixed-length code entered one digit per box. MuiOtpInput hands
            // back the value directly, so it needs setFieldValue, not handleChange.
            case "otp":
                return (
                    <FormControl sx={{ padding: 0, width: "100%" }}>
                        <FormLabel id={field.name} sx={{ fontWeight: field?.labelFontWeight, width: "100%" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <Typography>{field.label}</Typography>
                                {field?.required && (
                                    <Typography sx={{ color: "#E20000", fontSize: "14px", fontWeight: 500 }}>
                                        *
                                    </Typography>
                                )}
                            </Box>
                        </FormLabel>

                        <MuiOtpInput
                            length={field?.length ?? 6}
                            value={formik.values[field.name] ?? ""}
                            onChange={(value: string) =>
                                formik.setFieldValue(
                                    field.name,
                                    field?.uppercase ? value.toUpperCase() : value
                                )
                            }
                            // Defaults to alphanumeric; pass numericOnly for a
                            // digits-only code such as the customer OTP.
                            validateChar={(char: string) =>
                                field?.numericOnly
                                    ? /^[0-9]$/.test(char)
                                    : /^[a-zA-Z0-9]$/.test(char)
                            }
                            TextFieldsProps={{
                                inputProps: {
                                    inputMode: field?.numericOnly ? "numeric" : "text",
                                    autoCapitalize: "characters",
                                },
                            }}
                            sx={{
                                gap: "8px",
                                marginTop: "10px",
                                input: { padding: "12px 0", fontWeight: 600, fontSize: "18px" },
                            }}
                        />

                        {field?.text && (
                            <Typography sx={{ fontSize: "12px", marginTop: "8px" }}>{field.text}</Typography>
                        )}
                        {formik.touched[field.name] && props.formik.errors[field.name] && (
                            <Typography className="error_message" sx={{ marginTop: "6px" }}>
                                {props.formik.errors[field.name]}
                            </Typography>
                        )}
                    </FormControl>
                )

            case "select":
                return (
                    <SelectInput
                        name={field.name}
                        label={field.label}
                        value={formik.values[field.name]}
                        onChange={formik.handleChange}
                        onOptionClick={field?.onOptionClick}
                        options={field?.options}
                        error={formik.touched[field.name] && Boolean(props.formik.errors[field.name])}
                        helperText={formik.touched[field.name] && props.formik.errors[field.name]}
                        style={field?.style}
                        disabled={field?.disabled}
                        placeholder={field?.placeholder}
                        required={field?.required}
                        subLabel={field?.subLabel}
                    />
                )
            case "segmented":
                return (
                    <SegmentedInput
                        name={field.name}
                        label={field.label}
                        value={formik.values[field.name]}
                        onChange={formik.handleChange}
                        options={field?.options}
                        error={formik.touched[field.name] && Boolean(props.formik.errors[field.name])}
                        helperText={formik.touched[field.name] && props.formik.errors[field.name]}
                        required={field?.required}
                        disabled={field?.disabled}
                        subLabel={field?.subLabel}
                        labelFontWeight={field?.labelFontWeight}
                        style={field?.style}
                    />
                )

            case "multiInputUploadImage":
                return (
                    <MultiInputUploadImage
                        disabled={field?.disabled}
                        label={field.label}
                        id={field.name}
                        name={field.name}
                        value={formik.values[field.name]}
                        onChange={formik.handleChange}
                        error={formik.touched[field.name] && Boolean(props.formik.errors[field.name])}
                        helperText={formik.touched[field.name] && props.formik.errors[field.name]}
                        style={field?.style}
                        icon={field?.icon}
                        placeholder={field?.placeholder}
                        text={field?.text}
                        type={field?.type}
                        multiInputData={field?.multiInputData}
                        description={field?.description}
                        subLabelStyle={field?.subLabelStyle}
                        endAdornmentText={field?.endAdornmentText}
                        handleModal={field?.handleModal}
                        selectedFiles={field?.selectedFiles}
                        setSelectedFiles={field?.setSelectedFiles}
                        imagesData={field?.imagesData}
                        setDeletedFiles={field?.setDeletedFiles}
                        isFetching={field?.isFetching}
                    />
                )
            case "uploadImageV2":
                return (
                    <UploadImageV2
                        name={field.name}
                        label={field.label}
                        maxFiles={field?.maxFiles}
                        required={field?.required}
                        disabled={field?.disabled}
                        error={formik.touched[field.name] && Boolean(props.formik.errors[field.name])}
                        helperText={formik.touched[field.name] && props.formik.errors[field.name]}
                        selectedFiles={field?.selectedFiles}
                        setSelectedFiles={field?.setSelectedFiles}
                        setDeletedFiles={field?.setDeletedFiles}
                        imagesData={field?.imagesData}
                        isFetching={field?.isFetching}
                        reportType={field?.reportType}
                        handleModal={field?.handleModal}
                        accept={field?.accept}
                        acceptLabel={field?.acceptLabel}
                        maxBytes={field?.maxBytes}
                        variant={field?.variant}
                    />
                )

            case "uploadImage":
                return (
                    <UploadImage
                        disabled={field?.disabled}
                        label={field.label}
                        id={field.name}
                        name={field.name}
                        value={formik.values[field.name]}
                        onChange={formik.handleChange}
                        error={formik.touched[field.name] && Boolean(props.formik.errors[field.name])}
                        helperText={formik.touched[field.name] && props.formik.errors[field.name]}
                        style={field?.style}
                        icon={field?.icon}
                        placeholder={field?.placeholder}
                        text={field?.text}
                        type={field?.type}
                        multiInputData={field?.multiInputData}
                        description={field?.description}
                        subLabelStyle={field?.subLabelStyle}
                        endAdornmentText={field?.endAdornmentText}
                        handleModal={field?.handleModal}
                        selectedFiles={field?.selectedFiles}
                        setSelectedFiles={field?.setSelectedFiles}
                        imagesData={field?.imagesData}
                        setDeletedFiles={field?.setDeletedFiles}
                        isFetching={field?.isFetching}
                    />
                )

            default:
                return (
                    <Input
                        disabled={field?.disabled}
                        required={field?.required}
                        label={field.label}
                        id={field.name}
                        name={field.name}
                        value={formik.values[field.name]}
                        // `pattern` only affects validation, not typing, so a
                        // digitsOnly field drops everything else as it is entered.
                        onChange={
                            field?.digitsOnly
                                ? (event: any) =>
                                      formik.setFieldValue(field.name, event.target.value.replace(/\D/g, ""))
                                : formik.handleChange
                        }
                        error={formik.touched[field.name] && Boolean(props.formik.errors[field.name])}
                        helperText={formik.touched[field.name] && props.formik.errors[field.name]}
                        style={field?.style}
                        icon={field?.icon}
                        placeholder={field?.placeholder}
                        text={field?.text}
                        type={field?.type}
                        subLabel={field?.subLabel}
                        description={field?.description}
                        subLabelStyle={field?.subLabelStyle}
                        endAdornmentText={field?.endAdornmentText}
                        inputProps={field?.inputProps}
                    />
                )
        }
    }

    // `visibleIf` is optional — a field without one is always shown, so existing
    // callers are unaffected. Hidden fields are not rendered at all; clearing the
    // stale value is the owning hook's job (see useSiteAndLoad).
    const isVisible = (field: any) =>
        typeof field?.visibleIf === "function" ? !!field.visibleIf(formik.values) : true

    return (
        <>
            {data.fields?.filter(isVisible).map((field: any, index: number) => {
                return field?.subChild ? (
                    createElement(Fragment, { key: field?.name ?? index }, renderInput(field))
                ) : (
                    <Box component="div" key={field?.name ?? index} sx={field?.style}>
                        {renderInput(field)}
                    </Box>
                )
            })}
        </>
    )
}

export default Form
